/**
 * Local storage utilities for onboarding wizard state
 */

const WIZARD_KEY = 'onboarding.wizard';

/**
 * Default wizard state
 */
const defaultWizard = {
  completedAt: null,
  dismissedAt: null,
  stepIdx: 0,
  prefs: []
};

/**
 * Get wizard state from localStorage
 * @returns {Object} wizard state
 */
export const getWizard = () => {
  try {
    const stored = localStorage.getItem(WIZARD_KEY);
    if (!stored) return { ...defaultWizard };
    const parsed = JSON.parse(stored);
    // Ensure all fields exist
    return {
      completedAt: parsed.completedAt || null,
      dismissedAt: parsed.dismissedAt || null,
      stepIdx: parsed.stepIdx || 0,
      prefs: parsed.prefs || []
    };
  } catch (error) {
    console.error('Error reading wizard state:', error);
    return { ...defaultWizard };
  }
};

/**
 * Set wizard state in localStorage
 * @param {Object} wizard - wizard state to save
 */
export const setWizard = (wizard) => {
  try {
    localStorage.setItem(WIZARD_KEY, JSON.stringify(wizard));
  } catch (error) {
    console.error('Error saving wizard state:', error);
  }
};

/**
 * Mark wizard as dismissed (for 7 days)
 */
export const setWizardDismissedNow = () => {
  const wizard = getWizard();
  wizard.dismissedAt = new Date().toISOString();
  setWizard(wizard);
};

/**
 * Mark wizard as completed (never show again)
 */
export const markWizardCompleted = () => {
  const wizard = getWizard();
  wizard.completedAt = new Date().toISOString();
  setWizard(wizard);
};

/**
 * Update wizard step index
 * @param {number} idx - step index
 */
export const setWizardStep = (idx) => {
  const wizard = getWizard();
  wizard.stepIdx = idx;
  setWizard(wizard);
};

/**
 * Update wizard preferences
 * @param {string[]} prefs - array of sandwich type preferences
 */
export const setWizardPrefs = (prefs) => {
  const wizard = getWizard();
  wizard.prefs = prefs;
  setWizard(wizard);
};

/**
 * Check if wizard should be shown
 * @returns {boolean} true if wizard should be shown
 */
export const shouldShowWizard = () => {
  const wizard = getWizard();
  
  // Never show if completed
  if (wizard.completedAt) {
    return false;
  }
  
  // Check if dismissed within 7 days
  if (wizard.dismissedAt) {
    const dismissedDate = new Date(wizard.dismissedAt);
    const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
    if (daysSinceDismissed < 7) {
      return false;
    }
  }
  
  return true;
};

/**
 * Reset wizard state (for testing)
 */
export const resetWizard = () => {
  localStorage.removeItem(WIZARD_KEY);
};

